// ─────────────────────────────────────────────────────────────────
// Sehat AI — useVoiceConversation Hook
//
// Continuous listen → debounce → send → AI speaks → listen again
//
// Key design:
//   • continuous: true   — mic stays open; browser doesn't cut off mid-sentence
//   • 2.5 s debounce     — auto-sends only after 2.5 s of real silence
//   • Transcript merges  — multi-sentence speech works naturally
//   • sendNow()          — instant send if user wants to submit early
//   • isSendPending      — exposed so overlay can show "about to send" state
// ─────────────────────────────────────────────────────────────────

import { useState, useRef, useCallback, useEffect } from 'react';

const SR = window.SpeechRecognition || window.webkitSpeechRecognition;

// How long to wait after the last spoken word before auto-sending (ms)
const SEND_DELAY    = 2500;
// Grace period before restarting the mic after AI finishes speaking (ms)
const RESUME_DELAY  = 700;
// Restart delay after a non-fatal error or empty-speech cycle (ms)
const RETRY_DELAY   = 800;
// Max consecutive not-allowed / service errors before giving up
const MAX_ERRORS    = 3;

export function useVoiceConversation({ lang, onSend, isSpeaking }) {
  const [active, setActive]           = useState(false);
  const [listening, setListening]     = useState(false);
  const [interim, setInterim]         = useState('');
  const [isSendPending, setIsSendPending] = useState(false);

  const activeRef      = useRef(false);
  const speakingRef    = useRef(false);
  const onSendRef      = useRef(onSend);
  const recRef         = useRef(null);
  const transcriptRef  = useRef('');
  const restartTimer   = useRef(null);
  const sendTimer      = useRef(null);   // debounce before auto-send
  const errorCountRef  = useRef(0);      // consecutive fatal errors on mobile

  activeRef.current   = active;
  speakingRef.current = isSpeaking;
  onSendRef.current   = onSend;

  // ── Flush + send whatever is in the transcript buffer ───────────
  const flushAndSend = useCallback(() => {
    clearTimeout(sendTimer.current);
    setIsSendPending(false);
    const text = transcriptRef.current.trim();
    transcriptRef.current = '';
    if (text && activeRef.current && !speakingRef.current) {
      onSendRef.current(text);
    }
  }, []);

  // ── Stop recognition (fires onend which restarts the loop) ──────
  const stopListening = useCallback(() => {
    clearTimeout(restartTimer.current);
    clearTimeout(sendTimer.current);
    setIsSendPending(false);
    try { recRef.current?.stop(); } catch {}
    setListening(false);
  }, []);

  // ── Start continuous recognition ────────────────────────────────
  const startListening = useCallback(() => {
    if (!recRef.current || speakingRef.current || !activeRef.current) return;
    transcriptRef.current = '';
    setInterim('');
    setIsSendPending(false);
    try {
      recRef.current.start();
    } catch {
      // already running — ignore
    }
  }, []);

  // ── Send immediately (e.g. tap-to-send button) ──────────────────
  const sendNow = useCallback(() => {
    clearTimeout(sendTimer.current);
    // Stop recognition — onend will call flushAndSend via the flag
    try { recRef.current?.stop(); } catch {}
  }, []);

  // ── Build / rebuild recognition instance on lang change ─────────
  useEffect(() => {
    if (!SR) return;

    const rec = new SR();
    rec.continuous      = true;   // mic stays open across natural pauses
    rec.interimResults  = true;
    rec.maxAlternatives = 1;
    rec.lang = lang === 'hi' ? 'hi-IN' : 'en-IN';

    rec.onstart = () => {
      setListening(true);
      setInterim('');
      setIsSendPending(false);
    };

    rec.onresult = (e) => {
      let interimText = '';
      let finalText   = '';

      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText   += e.results[i][0].transcript;
        else                       interimText += e.results[i][0].transcript;
      }

      if (finalText) {
        transcriptRef.current = (transcriptRef.current + ' ' + finalText).trim();

        // Reset the 2.5 s auto-send debounce every time new words arrive
        clearTimeout(sendTimer.current);
        setIsSendPending(true);
        sendTimer.current = setTimeout(() => {
          // 2.5 s of silence → stop recognition; onend will send the text
          try { recRef.current?.stop(); } catch {}
        }, SEND_DELAY);
      }

      setInterim(interimText || transcriptRef.current);
    };

    rec.onend = () => {
      setListening(false);
      setInterim('');
      clearTimeout(sendTimer.current);
      setIsSendPending(false);

      const text = transcriptRef.current.trim();
      transcriptRef.current = '';

      if (text && activeRef.current && !speakingRef.current) {
        // Send to AI then wait for AI to reply (mic will restart via isSpeaking effect)
        onSendRef.current(text);
      } else if (activeRef.current && !speakingRef.current) {
        // Nothing was heard — restart quickly so user can try again
        restartTimer.current = setTimeout(() => {
          if (activeRef.current && !speakingRef.current) startListening();
        }, RETRY_DELAY);
      }
    };

    rec.onerror = (e) => {
      setListening(false);
      clearTimeout(sendTimer.current);
      setIsSendPending(false);

      // aborted = we called abort() ourselves (e.g. AI speaking) — not a real error
      if (e.error === 'aborted') return;

      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        errorCountRef.current += 1;
        // On mobile the first 'not-allowed' is often a timing issue — retry once
        if (errorCountRef.current <= MAX_ERRORS) {
          restartTimer.current = setTimeout(() => {
            if (activeRef.current && !speakingRef.current) startListening();
          }, RETRY_DELAY * 2);
          return;
        }
        // Truly denied after retries — give up and show message
        setActive(false);
        activeRef.current = false;
        alert('Microphone access denied. Please allow mic permission in your browser settings and try again.');
        return;
      }

      // Non-fatal (no-speech, network blip) — restart after short delay
      restartTimer.current = setTimeout(() => {
        if (activeRef.current && !speakingRef.current) startListening();
      }, RETRY_DELAY);
    };

    recRef.current = rec;

    return () => {
      clearTimeout(sendTimer.current);
      try { rec.abort(); } catch {}
    };
  }, [lang, startListening, flushAndSend]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pause mic while AI speaks; resume when AI finishes ──────────
  useEffect(() => {
    if (isSpeaking) {
      clearTimeout(restartTimer.current);
      clearTimeout(sendTimer.current);
      setIsSendPending(false);
      try { recRef.current?.abort(); } catch {}
      setListening(false);
    } else if (active) {
      restartTimer.current = setTimeout(() => {
        if (activeRef.current && !speakingRef.current) startListening();
      }, RESUME_DELAY);
    }
    return () => clearTimeout(restartTimer.current);
  }, [isSpeaking, active, startListening]);

  // ── Public API ──────────────────────────────────────────────────
  const enable = useCallback(() => {
    if (!SR) {
      alert('Aapke browser mein voice support nahi hai. Chrome ya Edge use karein.');
      return;
    }
    // NOTE: Do NOT call getUserMedia() here — on mobile it conflicts with
    // SpeechRecognition which manages its own mic access. Calling both at
    // the same time causes an immediate 'not-allowed' error on Android Chrome.
    errorCountRef.current = 0;
    setActive(true);
    activeRef.current = true;
    // Small delay so the overlay animation plays before mic activates
    setTimeout(startListening, 400);
  }, [startListening]);

  const disable = useCallback(() => {
    clearTimeout(restartTimer.current);
    clearTimeout(sendTimer.current);
    errorCountRef.current = 0;
    setActive(false);
    activeRef.current = false;
    stopListening();
    setInterim('');
    setIsSendPending(false);
    transcriptRef.current = '';
  }, [stopListening]);

  return {
    active,
    listening,
    interim,
    isSendPending,
    sendNow,
    supported: !!SR,
    enable,
    disable,
  };
}
